import { ApolloError, ApolloQueryResult, useLazyQuery } from "@apollo/client";
import moment from 'moment'
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { Flex, Text, Heading, Box, Card } from "rebass/styled-components";
import styled from "styled-components";
import Modal from "styled-react-modal";
import { Skeleton } from "../../components/loadingSkeleton/Skeleton";
import PleaseWait from "../../components/PleaseWait";
import { GET_CONVERSATIONS_BY_APPOINTMENT_ID } from "../../graphql/queries";
import { ICalendlyEvent, ILastSeen, ILastSeenInput, ILastSeenOutput } from "../../graphql/types/queries/appointment";
import { IGetProviderConversationsByAppointmentIdInput, IGetProviderConversationsByAppointmentIdOutput } from "../../graphql/types/queries/conversation";
import { Constants } from "../../utils/Constants";
import { getMockedFirstName, getMockedLastName } from "../../utils/mocks";
import { ToolbarMenuButton } from "../messages/components/ConversationToolbar";

const Wrapper = styled(Flex)`
  overflow: auto;
  flex-direction: column;
  padding-right: 12px;
  overflow-x: hidden;
  flex: 1;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

interface LastSeenProps {
  data: ILastSeenOutput | undefined,
  loading: boolean,
  error: ApolloError | undefined,
  refetch: (variables?: Partial<ILastSeenInput> | undefined) => Promise<ApolloQueryResult<ILastSeenOutput>>
}

const HoverableCard = styled(Card)`
  transition:all 0.3s ease;
  flex: initial;
`

const Row = ({ row, onGoToMessages, onGoToProfile, hoveredId, setHover }: { row: ICalendlyEvent, onGoToMessages: () => void, setHover: (id: string | null) => void, onGoToProfile: () => void, hoveredId: string | null }) => {
  const [invitee] = useState(row.invitees && row.invitees.length > 0 ? row.invitees[0] : null)
  const [user] = useState(row.invitees && row.invitees.length > 0 ? row.invitees[0].user : null)
  let patientName = 'unknown'
  let homephone: string | null = null
  
  if (invitee) {
    if (!!user) {
      patientName = `${(user.firstName || '').charAt(0)}. ${user.lastName}`
    } else {
      if (!!invitee.firstName && invitee.lastName) {
        patientName = `${(invitee.firstName || '').charAt(0)}. ${invitee.lastName}`
      } else {
        patientName = invitee.name
      }
    }
  }
  if (Constants.IS_SCRAMBLING_MODE) {
    patientName = `${getMockedFirstName()} ${getMockedLastName()}`
  }
  return (
    <HoverableCard onClick={onGoToProfile} mb={2} style={{ opacity: !hoveredId || hoveredId === row.uri ? 1 : 0.7, boxShadow: !hoveredId || hoveredId === row.uri ? '0px 4px 20px rgb(0 0 0 / 10%)' : '0px 4px 20px rgb(0 0 0 / 0%)' }} onMouseEnter={() => setHover(row.uri)} onMouseLeave={() => setHover(null)}>
      <Flex style={{ width: '100%', minHeight: '82px', padding: '18px 12px' }} >
        <Flex flex={1} flexDirection={['column', 'row', 'row']}>
          <Flex flex={1}>
            <Box width={'80px'} style={{ alignSelf: 'center' }}>
              <Text textAlign={'center'} style={{ color: '#7992B3', fontSize: '14px', fontWeight: 600 }}>{user?.athenaId || 'unknown'}</Text>
            </Box>
            <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
            <Box style={{ flex: 1, paddingLeft: '12px', alignSelf: 'center' }}>
              <Text style={{ color: '#7992B3', fontSize: '14px', fontWeight: 600 }}>{`${patientName}`}</Text>
            </Box>
          </Flex>
          <Box style={{ background: '#E2E2E2' }} height={['12px', '100%', '100%']} width={[0, '1px', '1px']}></Box>
          <Flex width={'280px'} paddingLeft={['24px', '12px', '12px']} style={{ alignSelf: 'center' }}>
            <Flex flexDirection={['row', 'column', 'column']} flex={1} justifyContent={['center', 'initial', 'initial']}>
              <Text style={{ color: '#7992B3', fontSize: '12px' }}>{moment(row.startTime).format('MM/DD/YYYY')}</Text>
              <Text ml={[1, 0, 0]} style={{ color: '#7992B3', fontSize: '12px' }}>{`${moment(row.startTime).format('hh:mmA')} - ${moment(row.endTime).format('hh:mmA')}`}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex style={{ paddingLeft: '24px' }} alignItems='center' flexDirection={['column', 'row', 'row']}>
          <svg style={{ cursor: 'pointer' }} onClick={e=>{
            e.preventDefault()
            e.stopPropagation()
            onGoToMessages()
          }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C4.20435 3 3.44129 3.31607 2.87868 3.87868C2.31607 4.44129 2 5.20435 2 6V21C2.00031 21.1772 2.04769 21.3511 2.1373 21.504C2.22691 21.6569 2.35553 21.7832 2.51 21.87C2.65946 21.9547 2.82821 21.9995 3 22C3.17948 21.9999 3.35564 21.9516 3.51 21.86L8 19.14C8.16597 19.0412 8.35699 18.9926 8.55 19H19C19.7956 19 20.5587 18.6839 21.1213 18.1213C21.6839 17.5587 22 16.7956 22 16V6C22 5.20435 21.6839 4.44129 21.1213 3.87868C20.5587 3.31607 19.7956 3 19 3ZM8 12C7.80222 12 7.60888 11.9414 7.44443 11.8315C7.27998 11.7216 7.15181 11.5654 7.07612 11.3827C7.00043 11.2 6.98063 10.9989 7.01921 10.8049C7.0578 10.6109 7.15304 10.4327 7.29289 10.2929C7.43275 10.153 7.61093 10.0578 7.80491 10.0192C7.99889 9.98063 8.19996 10.0004 8.38268 10.0761C8.56541 10.1518 8.72159 10.28 8.83147 10.4444C8.94135 10.6089 9 10.8022 9 11C9 11.2652 8.89464 11.5196 8.70711 11.7071C8.51957 11.8946 8.26522 12 8 12ZM12 12C11.8022 12 11.6089 11.9414 11.4444 11.8315C11.28 11.7216 11.1518 11.5654 11.0761 11.3827C11.0004 11.2 10.9806 10.9989 11.0192 10.8049C11.0578 10.6109 11.153 10.4327 11.2929 10.2929C11.4327 10.153 11.6109 10.0578 11.8049 10.0192C11.9989 9.98063 12.2 10.0004 12.3827 10.0761C12.5654 10.1518 12.7216 10.28 12.8315 10.4444C12.9414 10.6089 13 10.8022 13 11C13 11.2652 12.8946 11.5196 12.7071 11.7071C12.5196 11.8946 12.2652 12 12 12ZM16 12C15.8022 12 15.6089 11.9414 15.4444 11.8315C15.28 11.7216 15.1518 11.5654 15.0761 11.3827C15.0004 11.2 14.9806 10.9989 15.0192 10.8049C15.0578 10.6109 15.153 10.4327 15.2929 10.2929C15.4327 10.153 15.6109 10.0578 15.8049 10.0192C15.9989 9.98063 16.2 10.0004 16.3827 10.0761C16.5654 10.1518 16.7216 10.28 16.8315 10.4444C16.9414 10.6089 17 10.8022 17 11C17 11.2652 16.8946 11.5196 16.7071 11.7071C16.5196 11.8946 16.2652 12 16 12Z" fill="#344F7A" />
          </svg>
          <Box width={[0, '24px', '24px']} />
          <svg onClick={onGoToProfile} width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'pointer' }}>
            <path d="M28.0078 20.1123L28.0049 20.1094L28.0019 20.1064C26.7099 17.373 24.709 15.9668 22.0459 15.9668C19.3828 15.9668 17.3818 17.376 16.0869 20.1064V20.1094C15.9287 20.4463 15.9287 20.8389 16.0869 21.1758C17.3818 23.9062 19.3828 25.3125 22.0459 25.3125C24.709 25.3125 26.7099 23.9033 28.0049 21.1729C28.1631 20.8359 28.1631 20.4463 28.0078 20.1123ZM22.0459 23.4375C20.2266 23.4375 18.8994 22.5586 17.9121 20.6396C18.8965 18.7207 20.2266 17.8418 22.0459 17.8418C23.8652 17.8418 25.1924 18.7207 26.1797 20.6396C25.1953 22.5586 23.8652 23.4375 22.0459 23.4375Z" fill="#344F7A" />
            <path d="M20.4199 20.6543C20.4199 21.0894 20.5928 21.5067 20.9004 21.8144C21.2081 22.1221 21.6254 22.2949 22.0605 22.2949C22.4957 22.2949 22.913 22.1221 23.2206 21.8144C23.5283 21.5067 23.7012 21.0894 23.7012 20.6543C23.7012 20.2192 23.5283 19.8019 23.2206 19.4942C22.913 19.1865 22.4957 19.0137 22.0605 19.0137C21.6254 19.0137 21.2081 19.1865 20.9004 19.4942C20.5928 19.8019 20.4199 20.2192 20.4199 20.6543ZM3.98438 6.79688H24.6094V14.209H26.7188V5.625C26.7188 5.10645 26.2998 4.6875 25.7812 4.6875H2.8125C2.29395 4.6875 1.875 5.10645 1.875 5.625V20.8594C1.875 21.3779 2.29395 21.7969 2.8125 21.7969H13.125V19.6875H3.98438V6.79688Z" fill="#344F7A" />
            <path d="M21.2373 9.90528L20.1592 8.82715C20.0683 8.73633 19.9189 8.73633 19.8281 8.82715L14.4433 14.2178L11.9209 11.6924C11.8301 11.6016 11.6807 11.6016 11.5898 11.6924L7.3623 15.9199C7.27148 16.0108 7.27148 16.1602 7.3623 16.251L8.44043 17.3291C8.53125 17.4199 8.68066 17.4199 8.77148 17.3291L11.7539 14.3467L14.2764 16.8721C14.3672 16.9629 14.5166 16.9629 14.6074 16.8721L21.2373 10.2363C21.331 10.1455 21.331 9.9961 21.2373 9.90528Z" fill="#344F7A" />
          </svg>
        </Flex>
      </Flex>
    </HoverableCard >
  )
}

const RowLoading = () => {
  return (
    <HoverableCard mb={2} style={{ opacity: 1, boxShadow: '0px 4px 20px rgb(0 0 0 / 10%)' }} onMouseEnter={() => { }} onMouseLeave={() => { }}>
      <Flex style={{ width: '100%', minHeight: '82px', padding: '18px 12px' }}>
        <Box width={'80px'} style={{ alignSelf: 'center', paddingRight: '12px' }}>
          <Text textAlign={'center'} style={{ color: '#7992B3', fontSize: '12px' }}><Skeleton /></Text>
        </Box>
        <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
        <Box style={{ flex: 1, paddingLeft: '12px', alignSelf: 'center', paddingRight: '12px' }}>
          <Text style={{ color: '#7992B3', fontSize: '12px' }}><Skeleton /></Text>
        </Box>
        <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
        <Flex width={'280px'} style={{ paddingLeft: '12px', alignSelf: 'center' }}>
          <Flex flexDirection={'column'} flex={1}>
            <Text style={{ color: '#7992B3', fontSize: '12px' }}><Skeleton /></Text>
            <Text style={{ color: '#7992B3', fontSize: '12px' }}><Skeleton /></Text>
          </Flex>
          <Flex style={{ paddingLeft: '24px' }} alignItems='center'>

          </Flex>
        </Flex>
      </Flex>
    </HoverableCard>
  )
}

const Header = () => {
  return (
    <Flex style={{ width: '100%', minHeight: '82px', padding: '18px 12px' }}>
      <Box width={'80px'} style={{ alignSelf: 'center' }}>
        <Flex flexDirection={'row'} justifyContent='center'>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.75 15.75L12.3855 12.3795L15.75 15.75ZM14.25 7.875C14.25 9.56576 13.5784 11.1873 12.3828 12.3828C11.1873 13.5784 9.56576 14.25 7.875 14.25C6.18424 14.25 4.56274 13.5784 3.36719 12.3828C2.17165 11.1873 1.5 9.56576 1.5 7.875C1.5 6.18424 2.17165 4.56274 3.36719 3.36719C4.56274 2.17165 6.18424 1.5 7.875 1.5C9.56576 1.5 11.1873 2.17165 12.3828 3.36719C13.5784 4.56274 14.25 6.18424 14.25 7.875V7.875Z" stroke="#344F7A" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" />
          </svg>

          <Text textAlign={'center'} style={{ color: '#7992B3', fontSize: '12px' }} ml={2}>ID</Text>
        </Flex>
      </Box>
      <Box style={{ width: '1px', background: 'transparent' }}></Box>
      <Box style={{ flex: 1, paddingLeft: '12px', alignSelf: 'center' }}>
        <Flex flexDirection={'row'}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.75 15.75L12.3855 12.3795L15.75 15.75ZM14.25 7.875C14.25 9.56576 13.5784 11.1873 12.3828 12.3828C11.1873 13.5784 9.56576 14.25 7.875 14.25C6.18424 14.25 4.56274 13.5784 3.36719 12.3828C2.17165 11.1873 1.5 9.56576 1.5 7.875C1.5 6.18424 2.17165 4.56274 3.36719 3.36719C4.56274 2.17165 6.18424 1.5 7.875 1.5C9.56576 1.5 11.1873 2.17165 12.3828 3.36719C13.5784 4.56274 14.25 6.18424 14.25 7.875V7.875Z" stroke="#344F7A" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" />
          </svg>

          <Text style={{ color: '#7992B3', fontSize: '12px' }} ml={2}>Name</Text>
        </Flex>
      </Box>
      <Box style={{ width: '1px', background: 'transparent' }}></Box>
      <Box width={['initial', '280px', '280px']} style={{ paddingLeft: '12px', alignSelf: 'center' }}>
        <Flex flexDirection={'row'}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.28123 9H8.71872C9.38747 9 9.72185 9.80938 9.24997 10.2813L5.53123 14C5.23748 14.2937 4.76248 14.2937 4.47185 14L0.749977 10.2813C0.278102 9.80938 0.612477 9 1.28123 9ZM9.24997 5.71875L5.53123 2.00001C5.23748 1.70626 4.76248 1.70626 4.47185 2.00001L0.749977 5.71875C0.278102 6.19063 0.612477 7 1.28123 7H8.71872C9.38747 7 9.72185 6.19063 9.24997 5.71875Z" fill="#344F7A" fillOpacity="0.6" />
          </svg>

          <Text style={{ color: '#7992B3', fontSize: '12px' }} ml={2}>Visit</Text>
        </Flex>
      </Box>
    </Flex>
  )
}
export const LastSeen: React.FC<LastSeenProps> = ({ data, loading, error, refetch }) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const navigate = useNavigate()

  const [findConversation, { data: dataConvo, loading: loadingConvo, error: errorConvo }] = useLazyQuery<IGetProviderConversationsByAppointmentIdOutput, IGetProviderConversationsByAppointmentIdInput>(GET_CONVERSATIONS_BY_APPOINTMENT_ID, {
    onCompleted: (output) => {
      console.log({ output })
      if (!output || !output.healthCoachGetCareTeamConversation || !output.healthCoachGetCareTeamConversation || output.healthCoachGetCareTeamConversation.length < 1) return
      navigate(`/messages/${output.healthCoachGetCareTeamConversation[0].id}&s=true&target=ap`)
      // navigate(`/messages/142998&s=true&target=ap`)
    }
  })

  return (
    <Flex flexDirection={'column'} flex={1} mb={[3, 0, 0]} overflow={'hidden'}>
      <Flex>

        <Heading style={{ fontSize: '24px' }}>Last seen (past {Constants.LAST_SEEN_DAYS} days)</Heading>
        <ToolbarMenuButton data-tip='Refresh last seen' onClick={refetch} background='transparent'>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 11H7.101L7.102 10.991C7.23257 10.3516 7.48813 9.74434 7.854 9.20399C8.39845 8.4018 9.16215 7.77315 10.054 7.39299C10.356 7.26499 10.671 7.16699 10.992 7.10199C11.6579 6.96698 12.3441 6.96698 13.01 7.10199C13.967 7.29808 14.8451 7.7714 15.535 8.46299L16.951 7.05099C16.3128 6.41262 15.5578 5.90303 14.727 5.54999C14.3033 5.37062 13.8628 5.23394 13.412 5.14199C12.4818 4.953 11.5232 4.953 10.593 5.14199C10.1419 5.23432 9.70101 5.37133 9.277 5.55099C8.02753 6.08109 6.95793 6.96108 6.197 8.08499C5.68489 8.84284 5.32676 9.69398 5.143 10.59C5.115 10.725 5.1 10.863 5.08 11H2L6 15L10 11ZM14 13H16.899L16.898 13.008C16.6367 14.2897 15.8812 15.4171 14.795 16.146C14.2548 16.5122 13.6475 16.7677 13.008 16.898C12.3424 17.033 11.6566 17.033 10.991 16.898C10.3516 16.7674 9.74435 16.5119 9.204 16.146C8.93862 15.9665 8.69085 15.7622 8.464 15.536L7.05 16.95C7.68851 17.5882 8.44392 18.0974 9.275 18.45C9.699 18.63 10.142 18.767 10.59 18.858C11.5198 19.0471 12.4782 19.0471 13.408 18.858C15.2005 18.4859 16.7773 17.4294 17.803 15.913C18.3146 15.1557 18.6724 14.3053 18.856 13.41C18.883 13.275 18.899 13.137 18.919 13H22L18 8.99999L14 13Z" fill="black" />
          </svg>
        </ToolbarMenuButton>
      </Flex>
      <Wrapper>
        {(!!loading) && (
          <>
            <Header />
            {[1, 2, 3, 4].map(x => {
              return <RowLoading key={`loadingRow${x}`} />
            })}
          </>
        )}
        {!loading && !!data &&
          <>
            <Header />
            {data.healthCoachGetLastSeens.events.map(x => {
              return <Row
                hoveredId={hoveredRow}
                setHover={setHoveredRow}
                key={x.uri}
                row={x}
                onGoToMessages={() => {
                  console.log('onGoToMessages', { x: x.invitees })
                  if (!x.invitees || x.invitees.length < 1 || !x.invitees[0].user || !x.invitees[0].user.helpscoutId) return
                  findConversation({
                    variables: {
                      helpscoutId: x.invitees[0].user.helpscoutId
                    }
                  })
                }}
                onGoToProfile={() => {
                  if (!x.invitees || x.invitees.length < 1 || !x.invitees[0].user) return
                  navigate(`/patient/${x.invitees[0].user.athenaId}`)
                }} />
            })}
          </>
        }
      </Wrapper>
      {!!loadingConvo && (
        <Modal
          isOpen={true}>
          <Flex flexDirection={'column'}
            style={{
              margin: '0 auto',
              alignSelf: 'center',
              borderRadius: '20px',
              position: 'relative',
              backgroundColor: 'white',
              padding: '24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              maxHeight: 'calc(100vh * 0.8)',
            }}>
            <PleaseWait text="Searching for attached conversation" />
          </Flex>
        </Modal>
      )}
      <ReactTooltip place="bottom" type="dark" effect="float" />
    </Flex>);
}